import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Cloud, Download, Laptop, Loader2, RefreshCw, Trash2, Unlink } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { DEFAULT_CLOUD_URL, IS_DESKTOP, syncLabel } from "../../lib/desktop";
import { fmtFull } from "../../lib/format";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { LinkCloudForm } from "../DesktopFirstRun";

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-4 py-2 border-b last:border-0 text-sm">
    <span className="text-muted-foreground">{k}</span><span className="font-medium text-right break-all" data-testid={testid}>{v}</span>
  </div>
);

const DesktopSync = () => {
  const [s, setRaw] = useState(null);
  const [busy, setBusy] = useState("");
  const [url, setUrl] = useState("");
  const setS = useCallback((d) => { setRaw(d); window.dispatchEvent(new CustomEvent("mufa-sync", { detail: d })); }, []);
  const load = useCallback(() => api.get("/desktop/sync").then(({ data }) => { setS(data); setUrl(data.cloud_url || DEFAULT_CLOUD_URL); }), [setS]);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  const act = async (key, fn, msg) => {
    setBusy(key);
    try { const { data } = await fn(); setS(data); if (msg) toast.success(msg); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  if (!s) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!s.linked) return (
    <div className="max-w-xl space-y-4" data-testid="cloud-unlinked">
      <p className="text-sm text-muted-foreground">Aplikasi berjalan 100% lokal. Hubungkan ke cloud agar transaksi tersinkron otomatis dan bisa dipantau owner dari HP saat ada internet.</p>
      <LinkCloudForm allowRegister initialUrl={url} onDone={(d) => { setS(d); toast.success("Terhubung ke cloud"); }} />
    </div>
  );
  return (
    <div className="max-w-xl space-y-6" data-testid="cloud-linked">
      <div className="rounded-xl border p-4">
        <Row k="Status" v={syncLabel(s)} testid="cloud-status" />
        <Row k="Toko cloud" v={s.store_name || "-"} />
        <Row k="Sinkron terakhir" v={s.last_sync_at ? fmtFull(s.last_sync_at) : "-"} testid="cloud-last-sync" />
        {s.last_error && <Row k="Keterangan" v={s.last_error} testid="cloud-last-error" />}
        {(s.item_errors || []).map((e) => <Row key={e} k="Dilewati" v={e} />)}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => act("now", () => api.post("/desktop/sync/now"), "Sinkronisasi selesai")} disabled={!!busy} data-testid="cloud-sync-now-btn">
          {busy === "now" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}Sinkronkan Sekarang
        </Button>
        <Button variant="outline" onClick={() => window.confirm("Putuskan aplikasi ini dari cloud? Data lokal tetap aman.") && act("unlink", () => api.post("/desktop/sync/unlink"), "Terputus dari cloud")} disabled={!!busy} data-testid="cloud-unlink-btn">
          <Unlink className="h-4 w-4 mr-2" />Putuskan
        </Button>
      </div>
      <div className="space-y-2">
        <Label>Alamat server cloud</Label>
        <div className="flex gap-2">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="cloud-url-input" />
          <Button variant="outline" disabled={!!busy} onClick={() => act("url", () => api.post("/desktop/sync/url", { cloud_url: url }), "Alamat cloud diperbarui")} data-testid="cloud-url-save-btn">Simpan</Button>
        </div>
        <p className="text-xs text-muted-foreground">Ubah jika domain cloud berganti. Bila cloud tidak dapat diakses, aplikasi tetap berjalan normal dan data menunggu di komputer ini.</p>
      </div>
    </div>
  );
};

const WebDevices = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/sync/devices").then(({ data }) => setRows(data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const revoke = async (id) => {
    if (!window.confirm("Putuskan perangkat desktop ini dari cloud?")) return;
    await api.delete(`/sync/devices/${id}`);
    toast.success("Perangkat diputus");
    load();
  };
  return (
    <div className="max-w-2xl space-y-6">
      <div className="rounded-xl border p-5 flex flex-col sm:flex-row gap-4 sm:items-center">
        <Laptop className="h-10 w-10 text-primary shrink-0" />
        <div className="flex-1">
          <div className="font-semibold">Aplikasi Desktop Windows (offline penuh)</div>
          <p className="text-sm text-muted-foreground">Data tersimpan di komputer kasir, tetap jalan tanpa internet/domain, dan tersinkron ke sini saat online.</p>
        </div>
        <Button asChild data-testid="cloud-download-link"><a href="/unduh"><Download className="h-4 w-4 mr-2" />Unduh</a></Button>
      </div>
      <div>
        <h3 className="font-semibold mb-2 flex items-center gap-2"><Cloud className="h-4 w-4" />Perangkat desktop terhubung</h3>
        {rows === null ? <Loader2 className="h-5 w-5 animate-spin" /> : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="devices-empty">Belum ada perangkat desktop yang terhubung.</p>
        ) : rows.map((d) => (
          <div key={d.id} className="flex items-center gap-3 border rounded-lg px-4 py-3 mb-2" data-testid={`device-row-${d.id}`}>
            <div className="flex-1 text-sm"><div className="font-medium">{d.device_name}</div>
              <div className="text-muted-foreground text-xs">Aktif terakhir {fmtFull(d.last_seen)}</div></div>
            <Button size="icon" variant="ghost" onClick={() => revoke(d.id)} data-testid={`device-revoke-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CloudSyncTab = () => (IS_DESKTOP ? <DesktopSync /> : <WebDevices />);
